import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../auth/authContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getSubscriptionStatus, getTariffsList, initiateSubscriptionPayment } from '../../services/subscriptionApi';
import { resolveSubscriptionPaymentError } from '../../utils/subscriptionPaymentMessage';
import {
  getTenantDetail,
  getTenantSubscriptionTransactions,
  postTenantSubscriptionCancelRequest,
  postTenantSubscriptionPlanChangeRequest,
} from '../../services/tenantsApi';
import { formatMoneyUz } from '../../utils/formatMoneyUz';
import {
  prepayBaseTotalUzs,
  prepayTotalAfterDiscountUzs,
  prepayDiscountPercentLabel,
  SUBSCRIPTION_PREPAY_OPTIONS,
} from '../../utils/subscriptionPrepay';
import {
  type TariffRow,
  displayNameForPlanKey,
  monthlyPriceForPlanKey,
  planKeyFromTenant,
  tenantSubscriptionStatusLabel,
} from '../../utils/tariffPlanDisplay';
import './TariffPlanModal.css';

function optionValueForTariff(row: TariffRow): string {
  if (row.type_key && String(row.type_key).trim() !== '') {
    return String(row.type_key).trim();
  }
  return String(row.id);
}

function optionLabelForTariff(row: TariffRow, t: (k: string) => string, formatUzs: (n: number) => string): string {
  const price = Math.max(0, Math.round(Number(row.monthly_price ?? 0) || 0));
  const name =
    row.custom_name && String(row.custom_name).trim() !== ''
      ? String(row.custom_name).trim()
      : row.type_key
        ? t(`tariff_type_${row.type_key}`) !== `tariff_type_${row.type_key}`
          ? t(`tariff_type_${row.type_key}`)
          : String(row.type_key)
        : t('adminTariff_unnamed') || 'Tarif';
  return `${name} — ${formatUzs(price)} / ${t('subscription_months_unit')}`;
}

function fmtDate(v: unknown): string {
  if (v == null) return '—';
  const s = String(v).trim();
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('uz-UZ');
}

function ProviderLogo({ provider }: { provider: 'click' | 'payme' }) {
  if (provider === 'click') {
    return (
      <svg viewBox="0 0 96 28" className="admin-tariffplan-provider-logo" aria-hidden="true" focusable="false">
        <rect x="0.75" y="0.75" width="94.5" height="26.5" rx="8" fill="#ffffff" stroke="#e5e7eb" strokeWidth="1.5" />
        <circle cx="16" cy="14" r="7" fill="#00a6eb" />
        <text x="29" y="18" fontSize="11" fontWeight="800" fill="#00a6eb" fontFamily="Inter, Segoe UI, Arial, sans-serif">
          CLICK
        </text>
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 96 28" className="admin-tariffplan-provider-logo" aria-hidden="true" focusable="false">
      <rect x="0.75" y="0.75" width="94.5" height="26.5" rx="8" fill="#ffffff" stroke="#e5e7eb" strokeWidth="1.5" />
      <circle cx="16" cy="14" r="7" fill="#38bdf8" />
      <path d="M12.2 13.8h7.1" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M15.7 10.3v7.1" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" />
      <text x="29" y="18" fontSize="11" fontWeight="800" fill="#00a1df" fontFamily="Inter, Segoe UI, Arial, sans-serif">
        PAYME
      </text>
    </svg>
  );
}

export function TariffPlanModal({
  isOpen,
  onClose,
  forceLocked = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** Obuna muddati 1+ kun o‘tib, to‘lov bo‘lmaguncha yopish mumkin emas */
  forceLocked?: boolean;
}) {
  const { baseUser } = useAuth();
  const { t } = useLanguage();
  const tenantId = baseUser?.tenantId != null ? String(baseUser.tenantId) : '';

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [tenant, setTenant] = useState<Record<string, unknown> | null>(null);
  const [tx, setTx] = useState<Array<Record<string, unknown>>>([]);

  const [subStatus, setSubStatus] = useState<{
    status: string;
    daysUntilExpiry: number;
    isTrial: boolean;
    isExpired: boolean;
    endsAt: string | null;
    monthlyPrice: number;
  } | null>(null);

  const [months, setMonths] = useState(1);
  const [paymentProvider, setPaymentProvider] = useState<'click' | 'payme'>('click');
  const [applyCashback, setApplyCashback] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [payErr, setPayErr] = useState('');
  /** API: APP_URL host ≠ API_PUBLIC_URL — Click loglar boshqa serverda */
  const [clickHostHint, setClickHostHint] = useState('');
  const [pendingClickPayUrl, setPendingClickPayUrl] = useState('');
  /** API `click_checkout_diagnostic_hint` — backend OK, lekin checkout (-21) bo‘lsa kabinet/WAF */
  const [clickCheckoutDiag, setClickCheckoutDiag] = useState('');
  const payInFlightRef = useRef(false);

  const [cancelNote, setCancelNote] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);

  const [planChangeDesired, setPlanChangeDesired] = useState<string>('');
  const [planChangeNote, setPlanChangeNote] = useState('');
  const [planChangeLoading, setPlanChangeLoading] = useState(false);
  const [planChangeOk, setPlanChangeOk] = useState(false);
  const [tariffRows, setTariffRows] = useState<TariffRow[]>([]);

  const planKeyForPrepay = useMemo(() => planKeyFromTenant(tenant), [tenant]);

  const resolvedMonthlyPrice = useMemo(() => {
    const fromApi = Math.max(0, Math.round(Number(subStatus?.monthlyPrice) || 0));
    return monthlyPriceForPlanKey(planKeyForPrepay, tariffRows, fromApi);
  }, [planKeyForPrepay, tariffRows, subStatus?.monthlyPrice]);

  const canExtend = resolvedMonthlyPrice > 0;

  const currentPlanLabel = useMemo(
    () => displayNameForPlanKey(planKeyForPrepay, tariffRows, t),
    [planKeyForPrepay, tariffRows, t],
  );

  const cashbackEligible = useMemo(() => {
    const k = planKeyForPrepay;
    return k === 'businessMedium' || k === 'businessEnterprise';
  }, [planKeyForPrepay]);

  const cashbackBalance = useMemo(
    () => Math.max(0, Math.round(Number((tenant as { cashbackBalanceUzs?: number })?.cashbackBalanceUzs ?? 0))),
    [tenant],
  );

  const canRedeemCashback = useMemo(() => {
    if (!cashbackEligible) return false;
    const last = (tenant as { cashbackLastRedemptionAt?: string | null })?.cashbackLastRedemptionAt;
    if (last == null || String(last).trim() === '') return true;
    const d = new Date(String(last));
    if (Number.isNaN(d.getTime())) return true;
    return Date.now() >= d.getTime() + 365 * 24 * 60 * 60 * 1000;
  }, [tenant, cashbackEligible]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const ssResult = await Promise.allSettled([getSubscriptionStatus()]).then((r) => r[0]);
      const ss =
        ssResult.status === 'fulfilled'
          ? (ssResult.value as {
              status: string;
              daysUntilExpiry: number;
              isTrial: boolean;
              isExpired: boolean;
              endsAt: string | null;
              monthlyPrice: number;
              tenantId?: number | string | null;
            })
          : null;

      const resolvedTenantId =
        tenantId ||
        (ss?.tenantId != null && String(ss.tenantId).trim() !== '' ? String(ss.tenantId) : '');

      if (!resolvedTenantId && !ss) {
        setErr(t('tenant_tariff_no_tenant'));
        setTenant(null);
        setTx([]);
        setSubStatus(null);
        setTariffRows([]);
        return;
      }

      const [detailResult, rawTariffsResult] = await Promise.allSettled([
        resolvedTenantId ? getTenantDetail(resolvedTenantId) : Promise.resolve(null),
        getTariffsList(),
      ]);

      const detail =
        detailResult.status === 'fulfilled' ? (detailResult.value as Record<string, unknown> | null) : null;
      const rawTariffs =
        rawTariffsResult.status === 'fulfilled'
          ? (rawTariffsResult.value as TariffRow[])
          : [];

      if (!ss && !detail) {
        setErr(t('tenant_tariff_load_error'));
        setTenant(null);
        setTx([]);
        setSubStatus(null);
        setTariffRows([]);
        return;
      }

      const transactions = resolvedTenantId
        ? await getTenantSubscriptionTransactions(resolvedTenantId, { limit: 50 }).catch(
            () => [] as Array<Record<string, unknown>>,
          )
        : [];

      setTenant(detail);
      setTx(Array.isArray(transactions) ? transactions : []);
      setSubStatus(ss);
      const list = (Array.isArray(rawTariffs) ? rawTariffs : []) as TariffRow[];
      setTariffRows(
        list
          .slice()
          .sort((a, b) => {
            const oa = Number(a.sort_order) || 0;
            const ob = Number(b.sort_order) || 0;
            if (oa !== ob) return oa - ob;
            return Number(a.id) - Number(b.id);
          }),
      );
    } catch {
      setErr(t('tenant_tariff_load_error'));
      setTenant(null);
      setTx([]);
      setSubStatus(null);
      setTariffRows([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, t]);

  useEffect(() => {
    if (!isOpen) return;
    void load();
    setPlanChangeOk(false);
    setPayErr('');
    setClickHostHint('');
    setPendingClickPayUrl('');
    setClickCheckoutDiag('');
    setApplyCashback(false);
  }, [isOpen, load]);

  useEffect(() => {
    if (!isOpen || forceLocked) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, forceLocked]);

  const statusLabel = useMemo(() => {
    if (!subStatus) return '';
    if (subStatus.isExpired) return t('subscription_expired') || 'Obuna tugagan';
    if (subStatus.isTrial) return t('subscription_trial') || 'Sinov davri';
    return t('subscription_active') || 'Obuna faol';
  }, [subStatus, t]);

  const handlePay = useCallback(async () => {
    if (!subStatus) return;
    if (payInFlightRef.current) return;
    payInFlightRef.current = true;
    setPayLoading(true);
    setPayErr('');
    setClickHostHint('');
    setPendingClickPayUrl('');
    setClickCheckoutDiag('');
    try {
      const result = await initiateSubscriptionPayment(months, paymentProvider, applyCashback);
      if (!result.payUrl) {
        setPayErr(t('subscription_pay_err_no_url') || "To'lov havolasi yaratilmadi. Qaytadan urining.");
        return;
      }
      const selfTest = (result as { clickPrepareSelfTest?: Record<string, unknown> | null }).clickPrepareSelfTest;
      if (paymentProvider === 'click' && selfTest && selfTest.passed === false) {
        const parts = [
          typeof selfTest.hint === 'string' ? selfTest.hint : '',
          typeof selfTest.prepare_error_note === 'string' ? selfTest.prepare_error_note : '',
          selfTest.prepare_error != null ? `error: ${String(selfTest.prepare_error)}` : '',
        ].filter(Boolean);
        if (parts.length) {
          setPayErr(parts.join(' — '));
        }
      }
      if (paymentProvider === 'click' && result.clickOperatorNote) {
        setClickHostHint(result.clickOperatorNote);
        setPendingClickPayUrl(result.payUrl);
        const prepDiag = String((result as { clickCheckoutDiagnosticHint?: string }).clickCheckoutDiagnosticHint ?? '').trim();
        if (prepDiag) setClickCheckoutDiag(prepDiag);
        return;
      }
      const prepDiag = String((result as { clickCheckoutDiagnosticHint?: string }).clickCheckoutDiagnosticHint ?? '').trim();
      if (paymentProvider === 'click' && prepDiag) {
        try {
          console.warn('[Click checkout]', prepDiag);
        } catch {
          /* empty */
        }
      }
      // Faqat shu tabda ochiladi: alohida oyna/tab ochilmaydi.
      window.location.assign(result.payUrl);
    } catch (e: unknown) {
      setPayErr(resolveSubscriptionPaymentError(t, e));
    } finally {
      setPayLoading(false);
      payInFlightRef.current = false;
    }
  }, [months, subStatus, paymentProvider, applyCashback, t]);

  const handlePlanChangeRequest = useCallback(async () => {
    setPlanChangeLoading(true);
    setErr('');
    setPlanChangeOk(false);
    try {
      await postTenantSubscriptionPlanChangeRequest({
        desiredPlanKey: planChangeDesired || undefined,
        note: planChangeNote.trim() || undefined,
      });
      setPlanChangeOk(true);
      setPlanChangeNote('');
    } catch {
      setErr(t('tenant_tariff_plan_change_error'));
    } finally {
      setPlanChangeLoading(false);
    }
  }, [planChangeDesired, planChangeNote, t]);

  const handleCancelRequest = useCallback(async () => {
    setCancelLoading(true);
    setErr('');
    try {
      const next = await postTenantSubscriptionCancelRequest(cancelNote.trim() || undefined);
      setTenant(next as Record<string, unknown>);
      setCancelNote('');
      await load();
    } catch {
      setErr(t('tenant_tariff_cancel_error'));
    } finally {
      setCancelLoading(false);
    }
  }, [cancelNote, load, t]);

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      document.documentElement.style.setProperty('--tariff-vv-height', `${vv.height}px`);
      document.documentElement.style.setProperty('--tariff-vv-width', `${vv.width}px`);
      document.documentElement.style.setProperty('--tariff-vv-offset-top', `${vv.offsetTop}px`);
      document.documentElement.style.setProperty('--tariff-vv-offset-left', `${vv.offsetLeft}px`);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      ['--tariff-vv-height', '--tariff-vv-width', '--tariff-vv-offset-top', '--tariff-vv-offset-left'].forEach((k) =>
        document.documentElement.style.removeProperty(k),
      );
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="admin-sub-modal-overlay admin-sub-modal-overlay--tariff"
      onClick={forceLocked ? undefined : onClose}
      role="presentation"
    >
      <div className="admin-sub-modal admin-tariffplan-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="admin-tariffplan-head">
          <h3 className="admin-sub-modal-title">{t('adminNav_tariff_plan') || 'Tarif rejasi'}</h3>
          {forceLocked ? null : (
            <button type="button" className="admin-tariffplan-close-x" onClick={onClose} aria-label={t('adminBranch_close') || 'Yopish'}>
              ✕
            </button>
          )}
        </div>
        {forceLocked ? (
          <p className="admin-tariffplan-force-hint" role="status">
            {t('subscription_force_renew_hint') ||
              'Obuna muddati tugagan. Davom etish uchun tarifni yangilab, to‘lovni amalga oshiring — modalyopish mumkin emas.'}
          </p>
        ) : null}
        {!forceLocked && subStatus?.isExpired ? (
          <p className="admin-tariffplan-force-hint" role="status">
            {t('subscription_data_preserved_hint') ||
              'Ma\'lumotlaringiz (mahsulotlar, buyurtmalar, nasiya) saqlanadi. Yangi amallar uchun obunani yangilang.'}
          </p>
        ) : null}

        {loading ? <div className="admin-tariffplan-loading">{t('loginModalChecking')}</div> : null}
        {err ? <div className="admin-tariffplan-error" role="alert">{err}</div> : null}

        {!loading && (tenant || subStatus) ? (
          <div className="admin-tariffplan-body">
            {tenant ? (
            <section className="admin-tenant-tariff-card admin-tariffplan-section">
              <h4 className="admin-tariffplan-section-title">{t('tenant_tariff_status') || 'Obuna holati'}</h4>
              <dl className="admin-tenant-tariff-dl">
                <dt>{t('tenant_tariff_plan_key') || 'Tarif'}</dt>
                <dd className="admin-tenant-tariff-plan-name">{currentPlanLabel}</dd>
                <dt>{t('tenant_tariff_status_field') || 'Holat'}</dt>
                <dd>{tenantSubscriptionStatusLabel((tenant as { status?: unknown }).status, t)}</dd>
                <dt>{t('tenant_tariff_trial_ends') || 'Sinov tugash sanasi'}</dt>
                <dd>{fmtDate((tenant as any).trialEndsAt ?? (tenant as any).trial_ends_at)}</dd>
                <dt>{t('tenant_tariff_subscription_ends') || 'Obuna tugash sanasi'}</dt>
                <dd>{fmtDate((tenant as any).subscriptionEndsAt ?? (tenant as any).subscription_ends_at)}</dd>
                <dt>{t('tenant_tariff_cancel_requested') || 'Bekor qilish so‘rovi'}</dt>
                <dd>{fmtDate((tenant as any).cancellationRequestedAt ?? (tenant as any).cancellation_requested_at)}</dd>
              </dl>
            </section>
            ) : null}

            {subStatus ? (
              <section className="admin-tariffplan-section admin-tariffplan-section-extend">
                <h4 className="admin-sub-modal-extend-title">{t('subscription_extend')}</h4>
                <div className="admin-tariffplan-subinfo">
                  <div className="admin-tariffplan-subrow">
                    <span>{t('subscription_status_label')}</span>
                    <strong className={`admin-sub-status admin-sub-status--${subStatus.isExpired ? 'expired' : subStatus.isTrial ? 'trial' : 'active'}`}>
                      {statusLabel}
                    </strong>
                  </div>
                  <div className="admin-tariffplan-subrow">
                    <span>{t('subscription_days_remaining')}</span>
                    <strong className={subStatus.daysUntilExpiry <= 3 ? 'admin-sub-urgent' : ''}>
                      {subStatus.daysUntilExpiry} {t('subscription_days_unit')}
                    </strong>
                  </div>
                  {subStatus.endsAt ? (
                    <div className="admin-tariffplan-subrow">
                      <span>{t('subscription_ends_at')}</span>
                      <strong>{fmtDate(subStatus.endsAt)}</strong>
                    </div>
                  ) : null}
                  <div className="admin-tariffplan-subrow">
                    <span>{t('subscription_monthly_price')}</span>
                    <strong>{formatMoneyUz(resolvedMonthlyPrice)}</strong>
                  </div>
                  <p className="admin-tariffplan-prepay-hint">{t('subscription_prepay_matches_tariff')}</p>
                </div>

                <div className="admin-sub-months-grid admin-tariffplan-months-grid">
                  {SUBSCRIPTION_PREPAY_OPTIONS.map((m) => {
                    const base = prepayBaseTotalUzs(resolvedMonthlyPrice, m);
                    const finalUzs = prepayTotalAfterDiscountUzs(resolvedMonthlyPrice, m, planKeyForPrepay);
                    const disc = prepayDiscountPercentLabel(m, planKeyForPrepay);
                    return (
                      <button
                        key={m}
                        type="button"
                        className={`admin-sub-month-btn${months === m ? ' admin-sub-month-btn--active' : ''}`}
                        onClick={() => setMonths(m)}
                        disabled={!canExtend}
                      >
                        {disc ? <span className="admin-tariffplan-month-discount">{disc}</span> : null}
                        <span className="admin-sub-month-num">{m}</span>
                        <span className="admin-sub-month-label">{t('subscription_months_unit')}</span>
                        {disc && finalUzs < base ? (
                          <span className="admin-tariffplan-month-was" aria-hidden>
                            {formatMoneyUz(base)}
                          </span>
                        ) : null}
                        <span className="admin-sub-month-price admin-tariffplan-month-final">{formatMoneyUz(finalUzs)}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="admin-sub-modal-total">
                  <div className="admin-tariffplan-total-rows">
                    <div className="admin-tariffplan-total-line">
                      <span>{t('subscription_prepay_base')}</span>
                      <span>{formatMoneyUz(prepayBaseTotalUzs(resolvedMonthlyPrice, months))}</span>
                    </div>
                    {prepayDiscountPercentLabel(months, planKeyForPrepay) ? (
                      <div className="admin-tariffplan-total-line admin-tariffplan-total-line--disc">
                        <span>{t('subscription_prepay_discount_label')}</span>
                        <span>{prepayDiscountPercentLabel(months, planKeyForPrepay)}</span>
                      </div>
                    ) : null}
                    <div className="admin-tariffplan-total-line admin-tariffplan-total-line--strong">
                      <span>{t('subscription_total')}</span>
                      <strong>
                        {formatMoneyUz(prepayTotalAfterDiscountUzs(resolvedMonthlyPrice, months, planKeyForPrepay))}
                      </strong>
                    </div>
                  </div>
                </div>

                <div className="admin-sub-months-grid admin-tariffplan-months-grid">
                  {(['click', 'payme'] as const).map((provider) => (
                    <button
                      key={provider}
                      type="button"
                      className={`admin-sub-month-btn admin-tariffplan-provider-btn${
                        paymentProvider === provider ? ' admin-sub-month-btn--active' : ''
                      }`}
                      onClick={() => setPaymentProvider(provider)}
                      disabled={!canExtend || payLoading}
                    >
                      <ProviderLogo provider={provider} />
                      <span className="admin-tariffplan-provider-title">{provider === 'click' ? 'Click' : 'Payme'}</span>
                      <span className="admin-tariffplan-provider-subtitle">
                        {provider === 'click' ? 'Uzcard / Humo' : 'Payme checkout'}
                      </span>
                    </button>
                  ))}
                </div>

                <p className="admin-sub-modal-provider">
                  {`${t('subscription_provider')}: ${paymentProvider === 'click' ? 'Click' : 'Payme'} (${t('subscription_provider_new_tab')})`}
                </p>

                {clickHostHint ? (
                  <div className="admin-tariffplan-click-host-warn" role="status">
                    <p className="admin-tariffplan-click-hint admin-tariffplan-click-hint--warn">{clickHostHint}</p>
                    {clickCheckoutDiag ? (
                      <p className="admin-tariffplan-click-hint admin-tariffplan-click-hint--info" style={{ marginTop: '0.5rem' }}>
                        {clickCheckoutDiag}
                      </p>
                    ) : null}
                    {pendingClickPayUrl ? (
                      <button
                        type="button"
                        className="admin-sub-pay-btn admin-sub-pay-btn--secondary"
                        onClick={() => {
                          window.location.assign(pendingClickPayUrl);
                        }}
                      >
                        {t('subscription_click_continue_checkout')}
                      </button>
                    ) : null}
                  </div>
                ) : null}

                {payErr ? (
                  <p className="admin-tariffplan-error" role="alert" style={{ marginBottom: '0.5rem' }}>
                    {payErr}
                  </p>
                ) : null}

                {cashbackEligible ? (
                  <p className="admin-tariffplan-cashback-hint" style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                    {t('subscription_cashback_hint')}
                  </p>
                ) : null}
                {cashbackEligible && cashbackBalance > 0 ? (
                  <div className="admin-tariffplan-cashback-row" style={{ marginBottom: '0.35rem' }}>
                    <span>
                      {t('subscription_cashback_balance')}:{' '}
                      <strong>{formatMoneyUz(cashbackBalance)}</strong>
                    </span>
                  </div>
                ) : null}
                {cashbackEligible && canRedeemCashback && cashbackBalance > 0 ? (
                  <label
                    className="admin-tariffplan-cashback-check"
                    style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem' }}
                  >
                    <input
                      type="checkbox"
                      checked={applyCashback}
                      onChange={(e) => setApplyCashback(e.target.checked)}
                    />
                    <span>{t('subscription_cashback_apply')}</span>
                  </label>
                ) : null}
                {cashbackEligible &&
                !canRedeemCashback &&
                (tenant as { cashbackLastRedemptionAt?: string | null })?.cashbackLastRedemptionAt ? (
                  <p className="admin-tariffplan-cashback-next" style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                    {t('subscription_cashback_next')}:{' '}
                    {fmtDate((tenant as { cashbackLastRedemptionAt?: string }).cashbackLastRedemptionAt)}
                  </p>
                ) : null}

                <button
                  type="button"
                  className="admin-sub-pay-btn"
                  onClick={handlePay}
                  disabled={!canExtend || payLoading}
                >
                  {payLoading
                    ? t('common_loading')
                    : t('subscription_pay_btn')}
                </button>
              </section>
            ) : null}

            <section className="admin-tenant-tariff-card admin-tariffplan-section">
              <h4 className="admin-tariffplan-section-title">{t('tenant_tariff_plan_change_title')}</h4>
              <p className="admin-tariffplan-plan-change-lead">{t('tenant_tariff_plan_change_lead')}</p>
              <div className="admin-tariffplan-plan-change">
                <label className="admin-tariffplan-plan-change-label" htmlFor="plan-change-desired">
                  {t('tenant_tariff_plan_change_desired')}
                </label>
                <select
                  id="plan-change-desired"
                  className="admin-tariffplan-plan-change-select"
                  value={planChangeDesired}
                  onChange={(e) => setPlanChangeDesired(e.target.value)}
                >
                  <option value="">{t('tenant_tariff_plan_change_desired_placeholder')}</option>
                  {tariffRows.map((row) => {
                    const value = optionValueForTariff(row);
                    return (
                      <option key={row.id} value={value}>
                        {optionLabelForTariff(row, t, formatMoneyUz)}
                      </option>
                    );
                  })}
                </select>
                <label className="admin-tariffplan-plan-change-label" htmlFor="plan-change-note">
                  {t('tenant_tariff_plan_change_note')}
                </label>
                <textarea
                  id="plan-change-note"
                  className="admin-tariffplan-plan-change-textarea"
                  rows={3}
                  value={planChangeNote}
                  onChange={(e) => setPlanChangeNote(e.target.value)}
                  placeholder={t('tenant_tariff_plan_change_note_ph')}
                />
                {planChangeOk ? (
                  <p className="admin-tariffplan-plan-change-ok" role="status">
                    {t('tenant_tariff_plan_change_ok')}
                  </p>
                ) : null}
                <button
                  type="button"
                  className="admin-btn-secondary admin-tariffplan-plan-change-submit"
                  disabled={planChangeLoading || !String(planChangeDesired).trim()}
                  onClick={() => void handlePlanChangeRequest()}
                >
                  {planChangeLoading ? t('common_loading') : t('tenant_tariff_plan_change_submit')}
                </button>
              </div>
            </section>

            <section className="admin-tenant-tariff-card admin-tariffplan-section admin-tariffplan-section--cancel">
              <h4 className="admin-tariffplan-section-title admin-tariffplan-section-title--cancel">
                {t('tenant_tariff_cancel_title')}
              </h4>
              <p className="admin-tariffplan-cancel-lead">{t('tenant_tariff_cancel_lead')}</p>
              <div className="admin-tenant-tariff-cancel">
                <div className="admin-tenant-tariff-cancel-field">
                  <label className="admin-tenant-tariff-cancel-label" htmlFor="cancel-note">
                    {t('tenant_tariff_cancel_note')}
                  </label>
                  <textarea
                    id="cancel-note"
                    className="admin-tenant-tariff-cancel-textarea"
                    rows={3}
                    value={cancelNote}
                    onChange={(e) => setCancelNote(e.target.value)}
                    placeholder={t('tenant_tariff_cancel_placeholder')}
                  />
                </div>
                <div className="admin-tenant-tariff-cancel-actions">
                  <button
                    type="button"
                    className="admin-tariffplan-cancel-request-btn"
                    disabled={cancelLoading}
                    onClick={() => void handleCancelRequest()}
                  >
                    {cancelLoading ? t('common_loading') : t('tenant_tariff_cancel_submit')}
                  </button>
                </div>
              </div>
            </section>

            {tx.length > 0 ? (
              <section className="admin-tenant-tariff-card admin-tariffplan-section">
                <h4 className="admin-tariffplan-section-title">{t('tenant_tariff_transactions') || 'Tranzaksiyalar'}</h4>
                <ul className="admin-tenant-tariff-tx">
                  {tx.map((row) => (
                    <li key={String((row as any).id ?? `${(row as any).created_at ?? ''}-${Math.random()}`)}>
                      <span className="admin-tenant-tariff-tx-type">{String((row as any).type ?? '')}</span>
                      <span className="admin-tenant-tariff-tx-date">{fmtDate((row as any).created_at ?? (row as any).createdAt ?? '')}</span>
                      {(row as any).amount != null ? (
                        <span className="admin-tenant-tariff-tx-note">{formatMoneyUz(Number((row as any).amount) || 0)}</span>
                      ) : (row as any).note ? (
                        <span className="admin-tenant-tariff-tx-note">{String((row as any).note)}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </div>
        ) : null}

        {forceLocked ? null : (
          <button type="button" className="admin-sub-modal-close" onClick={onClose}>
            {t('adminBranch_cancel') || 'Yopish'}
          </button>
        )}
      </div>
    </div>
  );
}

